// src/pages/BrandAdminPage.jsx — /admin/brands
//
// EXITIUM ADMIN: which manufacturer brands each store carries. One grid,
// stores down the side, brands across the top, a switch in every cell.
// Flipping a switch writes a store_brands row through
// /api/phone-agents-2/store-brands (platform-admin only, by login email);
// the store's dashboard and phone agent pick it up on their next load.
//
// Needs the same login as /dashboard. Anyone who isn't a platform admin
// gets a plain "not for you" screen from the API's 403.

import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, AlertCircle, Package, ShieldCheck, LogIn } from "lucide-react";
import { supabase } from "../supabaseClient";

const API = "/api/phone-agents-2/store-brands";

const BRAND_CHIP = {
  ashley: "bg-blue-50 text-blue-800 border-blue-200",
  acme: "bg-violet-50 text-violet-800 border-violet-200",
  flexsteel: "bg-orange-50 text-orange-800 border-orange-200",
  newclassic: "bg-teal-50 text-teal-800 border-teal-200",
  coaster: "bg-amber-50 text-amber-800 border-amber-200",
  eastbay: "bg-slate-100 text-slate-700 border-slate-200",
};

const authHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const BrandAdminPage = () => {
  const [session, setSession] = useState(undefined); // undefined = checking
  const [brands, setBrands] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null); // 401 | 403 | null
  const [busy, setBusy] = useState({}); // `${storeId}|${brand}` -> true

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data?.session || null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s || null));
    return () => sub?.subscription?.unsubscribe();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API, { headers: await authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus(res.status);
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setStatus(null);
      setBrands(body.brands || []);
      setStores(body.stores || []);
    } catch (e) {
      setError(e?.message || "Could not load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) load();
  }, [session, load]);

  const toggle = async (store, brandKey) => {
    const key = `${store.id}|${brandKey}`;
    if (busy[key]) return;
    const next = !store.brands[brandKey];
    setBusy((b) => ({ ...b, [key]: true }));
    setError(null);
    // optimistic
    setStores((prev) => prev.map((s) => (s.id === store.id ? { ...s, brands: { ...s.brands, [brandKey]: next } } : s)));
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ store_id: store.id, brand: brandKey, enabled: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (e) {
      // roll back
      setStores((prev) => prev.map((s) => (s.id === store.id ? { ...s, brands: { ...s.brands, [brandKey]: !next } } : s)));
      setError(`${store.name}: ${e?.message || "save failed"}`);
    } finally {
      setBusy((b) => {
        const n = { ...b };
        delete n[key];
        return n;
      });
    }
  };

  // Prices: a store either has its own price rows (from a feed) or borrows
  // another store's list. The select writes copy_prices / clear_prices.
  const priceSources = stores.filter((s) => (s.prices || 0) > 0);
  const setPrices = async (store, fromId) => {
    const key = `${store.id}|prices`;
    if (busy[key]) return;
    const from = priceSources.find((s) => s.id === fromId);
    const msg = fromId
      ? `Copy ${from?.name}'s ${Number(from?.prices || 0).toLocaleString()} prices onto ${store.name}? Its agent will quote them on calls.`
      : `Remove all ${Number(store.prices || 0).toLocaleString()} prices from ${store.name}? Its agent will stop quoting prices.`;
    if (!window.confirm(msg)) return;
    setBusy((b) => ({ ...b, [key]: true }));
    setError(null);
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify(fromId
          ? { action: "copy_prices", store_id: store.id, from_store_id: fromId }
          : { action: "clear_prices", store_id: store.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setStores((prev) => prev.map((s) => (s.id === store.id ? { ...s, prices: body.prices } : s)));
    } catch (e) {
      setError(`${store.name}: ${e?.message || "price update failed"}`);
    } finally {
      setBusy((b) => {
        const n = { ...b };
        delete n[key];
        return n;
      });
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight">Brands by store</h1>
            <p className="text-[13px] text-slate-500">Exitium admin · which manufacturer catalogs each store carries</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  if (session === undefined) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-[13px] text-slate-500"><RefreshCw className="w-4 h-4 animate-spin" /> Checking your session…</div>
      </Shell>
    );
  }

  if (!session) {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-md">
          <LogIn className="w-5 h-5 text-slate-400 mb-2" />
          <p className="text-[14px] font-semibold">Log in first</p>
          <p className="text-[13px] text-slate-500 mt-1">Sign in at <a className="text-blue-600 underline" href="/dashboard">/dashboard</a> with your Exitium login, then come back to this page.</p>
        </div>
      </Shell>
    );
  }

  if (status === 403) {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-md">
          <p className="text-[14px] font-semibold">This page is for Exitium staff</p>
          <p className="text-[13px] text-slate-500 mt-1">Your login ({session.user?.email}) isn't on the admin list. Your own store's brands are shown in the dashboard under Phone Agent → Catalog &amp; Deals → Product Catalog.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="text-[13px] text-slate-600">
          A switch on means the store's dashboard shows that brand's products and its phone agent knows them.
          Prices: a store with price rows on file has its agent quote them; with none, the agent knows the product but won't quote a price.
          A store can borrow another store's price list (East Bay's comes from WonderSign and covers everything but Coaster).
        </p>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px] flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/70">
              <th className="text-left font-semibold text-slate-500 px-4 py-3 whitespace-nowrap">Store</th>
              {brands.map((b) => (
                <th key={b.key} className="px-3 py-3 text-center whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${BRAND_CHIP[b.key] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {b.label}
                    <span className="font-medium opacity-70 tabular-nums">{b.products == null ? "…" : b.products.toLocaleString()}</span>
                  </span>
                </th>
              ))}
              <th className="text-left font-semibold text-slate-500 px-4 py-3 whitespace-nowrap">Prices</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stores.map((s) => {
              const on = brands.filter((b) => s.brands[b.key]).length;
              return (
                <tr key={s.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="text-[11px] text-slate-400 tabular-nums">{on === 0 ? "no catalog brands" : `${on} brand${on === 1 ? "" : "s"} on`}</div>
                  </td>
                  {brands.map((b) => {
                    const checked = !!s.brands[b.key];
                    const key = `${s.id}|${b.key}`;
                    return (
                      <td key={b.key} className="px-3 py-3 text-center">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={checked}
                          aria-label={`${b.label} for ${s.name}`}
                          disabled={!!busy[key]}
                          onClick={() => toggle(s, b.key)}
                          className={`relative inline-block w-10 h-6 rounded-full transition-colors disabled:opacity-60 ${checked ? "bg-emerald-500" : "bg-slate-300"}`}
                        >
                          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-[12px] text-slate-700 tabular-nums">
                      {s.prices == null ? "…" : s.prices > 0 ? `${s.prices.toLocaleString()} on file` : <span className="text-slate-400">none</span>}
                    </div>
                    <select
                      value=""
                      disabled={!!busy[`${s.id}|prices`]}
                      onChange={(e) => { const v = e.target.value; if (v === "clear") setPrices(s, null); else if (v) setPrices(s, v); }}
                      className="mt-1 text-[11px] rounded-md border border-slate-200 bg-white px-1.5 py-1 text-slate-600 disabled:opacity-60"
                      aria-label={`Prices for ${s.name}`}
                    >
                      <option value="">{busy[`${s.id}|prices`] ? "Working…" : "Change…"}</option>
                      {priceSources.filter((src) => src.id !== s.id).map((src) => (
                        <option key={src.id} value={src.id}>Use {src.name}'s list ({Number(src.prices).toLocaleString()})</option>
                      ))}
                      {(s.prices || 0) > 0 && <option value="clear">Remove all prices</option>}
                    </select>
                  </td>
                </tr>
              );
            })}
            {!loading && stores.length === 0 && (
              <tr><td className="px-4 py-6 text-slate-500" colSpan={brands.length + 2}>No stores.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5">
        <Package className="w-3.5 h-3.5" />
        East Bay is East Bay Furniture's own line — leave it off for everyone else.
      </p>
    </Shell>
  );
};

export default BrandAdminPage;
